import { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import { scrollBarStyles } from '@/utils/theme';

import InvoiceHtml, { InvoiceHtmlProps } from './InvoiceHtml';

const ModalCard = styled.div`
  width: 100%;
  height: calc(100vh - 30px);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ScrollArea = styled.div`
  max-height: calc(100vh - 145px);
  overflow-y: auto;
  ${scrollBarStyles}
`;

const ModalFooter = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 21px 24px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  box-shadow:
    0 10px 30px 0 rgba(34, 34, 34, 0.12),
    0 20px 100px 0 rgba(34, 34, 34, 0.06);
  width: 100%;
  max-width: 1120px;
  gap: 16px;
`;

interface Props {
  onClose: () => void;
  onSubmit?: () => void;
  invoiceProps: InvoiceHtmlProps;
}

const InvoicePreviewModal: FC<Props> = ({
  onClose,
  onSubmit,
  invoiceProps,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  // The Modal overlay stops click propagation, so a document-level listener
  // never sees these clicks — listen on the portal container in capture phase.
  useEffect(() => {
    const portalContainer = cardRef.current?.parentElement?.parentElement;
    if (!portalContainer) return;

    const handleClick = (e: Event) => {
      const target = e.target as Node;
      if (
        !scrollRef.current?.contains(target) &&
        !footerRef.current?.contains(target)
      ) {
        onClose();
      }
    };

    portalContainer.addEventListener('click', handleClick, true);
    return () =>
      portalContainer.removeEventListener('click', handleClick, true);
  }, [onClose]);

  useEffect(() => {
    const callback = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onClose();
    };
    document.addEventListener('keydown', callback);
    return () => document.removeEventListener('keydown', callback);
  }, [onClose]);

  return (
    <Modal>
      <ModalCard ref={cardRef}>
        <ScrollArea ref={scrollRef}>
          <InvoiceHtml {...invoiceProps} />
        </ScrollArea>
        <ModalFooter ref={footerRef}>
          <Button
            color="secondary"
            onClick={onClose}
            style={{ background: 'transparent' }}
          >
            Close
          </Button>
          {onSubmit && <Button onClick={onSubmit}>Send Invoice</Button>}
        </ModalFooter>
      </ModalCard>
    </Modal>
  );
};

export default InvoicePreviewModal;
